import React, { useState, useEffect } from "react"
import Swal from "sweetalert2"
import Footer from "../components/Footer"
import { useOutletContext, useLocation, Link, useNavigate } from "react-router-dom"
import { confirmBooking, downloadTravelDocument, preValidateBookingLimits } from "../api/bookingApi"
import { isServerPriceCorrection, applyServerPriceCorrection } from "../utils/serverPriceCorrection"

const SearchResultVehicle = () => {
  const { companyInfo } = useOutletContext()
  const location = useLocation()
  const { searchResults: initialSearchResults, searchParams, ports, cabins, payloadTypes } = location.state || {}
  const [searchResults, setSearchResults] = useState(initialSearchResults)

  const [selectedTrips, setSelectedTrips] = useState([])
  const [expandedTrips, setExpandedTrips] = useState({})
  const [selectedPricingType, setSelectedPricingType] = React.useState({})

  const navigate = useNavigate()
  const [isBooking, setIsBooking] = useState(false)
  const isB2C = localStorage.getItem("authToken") && !localStorage.getItem("companyAuthToken");

  const createEmptyVehicleForm = (payloadType = null) => ({
    payloadTypeId: payloadType?._id || "",
    payloadTypeName: payloadType?.name || "Vehicle",
    vehicleType: payloadType?.name || "",
    vehicleRegistration: "",
    ownerName: "",
    ownerPhone: "",
    makeModel: "",
    engineNo: "",
    ownerPassport: "",
    ownerLicense: "",
    consigneeName: "",
    consigneePhone: "",
    consigneeIdNo: "",
    consignorName: "",
    consignorPhone: "",
    consignorIdNo: ""
  })

  const [vehicleFormData, setVehicleFormData] = useState({})

  const getExpectedVehicleSlots = () => {
    const slots = []
    searchParams?.passengers?.forEach((passenger, pIdx) => {
      const payloadType = payloadTypes?.find((p) => p._id === passenger.payloadTypeId)
      Array.from({ length: Number(passenger.quantity || 0) }).forEach((_, idx) => {
        slots.push({
          key: `${pIdx}-${idx}`,
          payloadType,
          sequence: idx + 1,
        })
      })
    })
    return slots
  }

  // Prefill when searchParams or visaType changes
  useEffect(() => {
    if (!searchParams || !payloadTypes) return
    const nextSlots = {}
    getExpectedVehicleSlots().forEach(({ key, payloadType }) => {
      nextSlots[key] = {
        ...createEmptyVehicleForm(payloadType),
        ...(vehicleFormData[key] || {}),
        payloadTypeId: payloadType?._id || vehicleFormData[key]?.payloadTypeId || "",
        payloadTypeName: payloadType?.name || vehicleFormData[key]?.payloadTypeName || "Vehicle",
        vehicleType: vehicleFormData[key]?.vehicleType || payloadType?.name || "",
      }
    })
    setVehicleFormData(nextSlots)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, payloadTypes])

  const handleVehicleChange = (vehicleKey, field, value) => {
    setVehicleFormData(prev => ({
      ...prev,
      [vehicleKey]: {
        ...(prev[vehicleKey] || createEmptyVehicleForm()),
        [field]: value
      }
    }))
  }

  const parseTripCabinKey = (key, prefix = "") => {
    const value = prefix ? key.slice(prefix.length) : key
    const separatorIndex = value.lastIndexOf("-")
    return {
      tripId: value.slice(0, separatorIndex),
      cabinId: value.slice(separatorIndex + 1)
    }
  }

  const findSelectedTripOption = (key) => {
    const isInbound = key.startsWith("inbound-")
    const { tripId, cabinId } = parseTripCabinKey(key, isInbound ? "inbound-" : "")
    return displayTrips.find(option => {
      const trip = isInbound ? option.returnTrip?.inbound : (option.trip || option.returnTrip?.outbound)
      return trip?._id === tripId && option.cabin?._id === cabinId
    })
  }

  const handlePreValidateBooking = async () => {
    const token = localStorage.getItem("companyAuthToken") || localStorage.getItem("authToken")
    if (!token) {
      Swal.fire({ icon: 'warning', title: 'Not Logged In', text: 'Authentication token not found. Please log in.' })
      return
    }

    if (selectedTrips.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Trip Selected', text: 'Please select a trip before booking.' })
      return
    }

    const isReturnTrip = searchParams?.tripType === 'return'
    const outboundKey = selectedTrips.find(key => !key.startsWith("inbound-"))
    const inboundKey = selectedTrips.find(key => key.startsWith("inbound-"))

    if (!outboundKey || (isReturnTrip && !inboundKey)) {
      Swal.fire({
        icon: 'error',
        title: 'Booking Blocked',
        text: isReturnTrip
          ? 'Select both outbound and inbound fares before booking.'
          : 'Select an outbound fare before booking.'
      })
      return
    }

    if (isReturnTrip && selectedPricingType[outboundKey] !== selectedPricingType[inboundKey]) {
      Swal.fire({
        icon: 'error',
        title: 'Booking Blocked',
        text: 'Select both outbound and inbound using the same fare type.'
      })
      return
    }

    let totalFareAggregate = 0
    let currencyCode = null
    let totalVehiclesQty = 0

    searchParams?.passengers?.forEach((passenger) => {
      totalVehiclesQty += Number(passenger.quantity || 0)
    })

    for (const key of selectedTrips) {
      const isInbound = key.startsWith("inbound-")
      const tripOption = findSelectedTripOption(key)
      const selectedType = selectedPricingType[key]
      const activePricing = selectedType === "refundable"
        ? tripOption?.pricing?.refundable
        : selectedType === "nonRefundable"
          ? tripOption?.pricing?.nonRefundable
          : null
      const legTotal = isReturnTrip
        ? (isInbound ? activePricing?.inboundTotal : activePricing?.outboundTotal)
        : activePricing?.totalPrice
      const legCurrencyCode = activePricing?.currency?.currencyCode

      if (!tripOption || !activePricing || !Number.isFinite(Number(legTotal)) || Number(legTotal) <= 0 || !legCurrencyCode) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Blocked',
          text: `Pricing could not be resolved for the selected ${isInbound ? "inbound" : "outbound"} trip. Please select the fare again.`
        })
        return
      }

      if (currencyCode && currencyCode !== legCurrencyCode) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Blocked',
          text: 'Selected trip fares use different currencies. Please select matching fares.'
        })
        return
      }

      currencyCode = legCurrencyCode
      totalFareAggregate += Number(legTotal)
    }

    // Skip validation for B2C, but show loading and check for B2B
    if (isB2C) {
      const modalElement = document.getElementById('myModal1')
      if (modalElement) {
        const modal = new window.bootstrap.Modal(modalElement)
        modal.show()
      }
      return
    }

    Swal.fire({
      title: 'Validating booking limits...',
      text: 'Checking your hierarchy balances...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const payload = {
      totalFare: totalFareAggregate,
      currencyCode,
      requiredTickets: totalVehiclesQty
    }

    try {
      const response = await preValidateBookingLimits(token, payload)
      if (response && response.success) {
        Swal.close()
        const modalElement = document.getElementById('myModal1')
        if (modalElement) {
          const modal = new window.bootstrap.Modal(modalElement)
          modal.show()
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Booking Blocked', text: response?.message || "Hierarchy validation failed." })
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || "An error occurred during validation." })
    }
  }

  const onSubmitBooking = async (isTemporary = false) => {
    try {
      const token = localStorage.getItem("companyAuthToken") || localStorage.getItem("authToken")
      if (!token) {
        Swal.fire({ icon: 'warning', title: 'Not Logged In', text: 'Authentication token not found. Please log in.' })
        return
      }

      if (selectedTrips.length === 0) {
        Swal.fire({ icon: 'warning', title: 'No Trip Selected', text: 'Please select a trip before booking.' })
        return
      }

      const vehicleSlots = getExpectedVehicleSlots()
      const vehicleEntries = vehicleSlots.map((slot) => ({
        ...slot,
        data: vehicleFormData[slot.key] || createEmptyVehicleForm(slot.payloadType)
      }))

      if (!vehicleEntries.length) {
        Swal.fire({ icon: 'warning', title: 'No Vehicle Selected', text: 'Please select at least one vehicle quantity before booking.' })
        return
      }

      const incompleteVehicle = vehicleEntries.find(({ data }) => (
        !data.vehicleRegistration ||
        !data.ownerName ||
        !data.ownerPhone ||
        !data.makeModel
      ))
      if (incompleteVehicle) {
        Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in all mandatory fields for every vehicle (Make/Model, Plate No, Owner Name, and Owner Phone).' })
        return
      }

      const isReturnTrip = searchParams?.tripType === 'return'
      const outboundKey = selectedTrips.find(key => !key.startsWith("inbound-"))
      const inboundKey = selectedTrips.find(key => key.startsWith("inbound-"))

      if (!outboundKey || (isReturnTrip && !inboundKey)) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: isReturnTrip
            ? 'Select both outbound and inbound fares before booking.'
            : 'Select an outbound fare before booking.'
        })
        return
      }

      const { tripId: outboundTripId, cabinId } = parseTripCabinKey(outboundKey)
      const inboundSelection = inboundKey
        ? parseTripCabinKey(inboundKey, "inbound-")
        : null
      const outboundTripObj = findSelectedTripOption(outboundKey)
      const inboundTripObj = inboundKey ? findSelectedTripOption(inboundKey) : null
      const outboundTrip = outboundTripObj?.trip || outboundTripObj?.returnTrip?.outbound
      const inboundTrip = inboundTripObj?.returnTrip?.inbound

      if (!outboundTripObj || !outboundTrip || (isReturnTrip && (!inboundTripObj || !inboundTrip))) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: 'The selected trip details could not be resolved. Please select the fares again.'
        })
        return
      }

      const outboundPricingType = selectedPricingType[outboundKey]
      const inboundPricingType = inboundKey ? selectedPricingType[inboundKey] : null

      if (
        !["refundable", "nonRefundable"].includes(outboundPricingType) ||
        (isReturnTrip && inboundPricingType !== outboundPricingType)
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: isReturnTrip
            ? 'Outbound and inbound must use the same selected fare type.'
            : 'The selected fare type could not be resolved. Please select the fare again.'
        })
        return
      }

      const activePricing = outboundTripObj.pricing?.[outboundPricingType]
      const totalFare = isReturnTrip ? activePricing?.returnTotalPrice : activePricing?.totalPrice
      const baseFare = activePricing?.totalBasicPrice
      const currencyId = activePricing?.currency?._id || (
        typeof activePricing?.currency === "string" ? activePricing.currency : null
      )

      if (
        !activePricing ||
        !Number.isFinite(Number(totalFare)) ||
        Number(totalFare) <= 0 ||
        !Number.isFinite(Number(baseFare)) ||
        Number(baseFare) <= 0 ||
        !currencyId
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: 'The selected fare has incomplete pricing or currency data. Please contact the company.'
        })
        return
      }

      const totalVehiclesQty = vehicleEntries.length
      const primaryVehicle = vehicleEntries[0].data
      const vehicleItems = vehicleEntries.map(({ data, payloadType, sequence }) => ({
        payloadTypeId: payloadType?._id || data.payloadTypeId || "",
        payloadTypeName: payloadType?.name || data.payloadTypeName || data.vehicleType || "Vehicle",
        sequence,
        type: data.vehicleType,
        vehicleType: data.vehicleType,
        vehicleRegistration: data.vehicleRegistration,
        registrationNumber: data.vehicleRegistration,
        plateNo: data.vehicleRegistration,
        weight: 0,
        makeModel: data.makeModel,
        engineNo: data.engineNo || "",
        engineChassis: data.engineNo || "",
        owner: {
          name: data.ownerName,
          phone: data.ownerPhone,
          passport: data.ownerPassport || "",
          license: data.ownerLicense || "",
          licenseNo: data.ownerLicense || "",
        },
        consignee: {
          name: data.consigneeName || "",
          phone: data.consigneePhone || "",
          idNo: data.consigneeIdNo || "",
        },
        consignor: {
          name: data.consignorName || "",
          phone: data.consignorPhone || "",
          idNo: data.consignorIdNo || "",
        },
      }))

      const payload = {
        category: "vehicle",
        bookingType: isReturnTrip ? "Return" : "OneWay",
        bookingSource: isB2C ? "Internal" : "Agent",
        outboundTrip: outboundTripId,
        returnTrip: isReturnTrip ? inboundSelection.tripId : null,
        originPort: searchParams?.originPort,
        destinationPort: searchParams?.destinationPort,
        departureDate: outboundTrip.departureDateTime,
        returnDate: isReturnTrip ? inboundTrip.departureDateTime : null,
        cabin: cabinId,
        visaType: (searchParams?.visaType && searchParams.visaType.toLowerCase() !== "all") ? searchParams.visaType : (outboundTripObj?.pricing?.refundable?.breakdown?.[0]?.visaType || outboundTripObj?.pricing?.visaType || "All"),
        currency: currencyId,
        baseFare: baseFare,
        totalFare: totalFare,
        isTemporary: !!isTemporary,
        vehicleType: primaryVehicle.vehicleType,
        vehicleRegistration: primaryVehicle.vehicleRegistration,
        vehicleModel: primaryVehicle.makeModel,
        ownerName: primaryVehicle.ownerName,
        ownerContactNumber: primaryVehicle.ownerPhone,
        vehicleDetails: {
          ...vehicleItems[0],
          quantity: totalVehiclesQty,
          items: vehicleItems
        },
        ownerDetails: {
          name: primaryVehicle.ownerName,
          phone: primaryVehicle.ownerPhone,
          passport: primaryVehicle.ownerPassport || "",
          licenseNo: primaryVehicle.ownerLicense || ""
        },
        consignorDetails: {
          name: primaryVehicle.consignorName || "",
          phone: primaryVehicle.consignorPhone || "",
          idNo: primaryVehicle.consignorIdNo || ""
        },
        consigneeDetails: {
          name: primaryVehicle.consigneeName || "",
          phone: primaryVehicle.consigneePhone || "",
          idNo: primaryVehicle.consigneeIdNo || ""
        },
        snapshot: {
          trip: outboundTrip,
          returnTrip: isReturnTrip ? inboundTrip : null,
          cabin: outboundTripObj?.cabin,
          passengers: outboundTripObj?.passengers,
          pricing: outboundTripObj?.pricing,
          ticketingRules: outboundTripObj?.ticketingRules,
          availability: outboundTripObj?.availability,
          selectedPricingType: outboundPricingType
        }
      }

      setIsBooking(true)
      const idempotencyKey = Date.now().toString() + Math.random().toString(36).substring(7)
      let response = await confirmBooking(token, payload, idempotencyKey)

      // Server-authoritative pricing: if the backend recomputed a different price, show the
      // corrected amount and re-submit once with the server values after the customer confirms.
      if (isServerPriceCorrection(response)) {
        const accepted = await applyServerPriceCorrection(response, payload)
        if (!accepted) return
        response = await confirmBooking(token, payload, idempotencyKey)
      }

      if (response && response.success) {
        let successTitle = isTemporary ? 'Booking Placed on Hold!' : 'Booking Confirmed!';
        let successText = isTemporary 
          ? 'Your booking is temporarily held for 15 minutes. Please complete payment in the booking list.' 
          : 'Your booking has been created successfully.';
        
        if (isB2C) {
          successTitle = 'Booking Request Received!';
          successText = 'Your booking is placed on hold pending review and confirmation by the company.';
        }

        const createdBooking = response.data?.booking || response.data
        const canDownloadTravelDocument = !isTemporary && !isB2C && createdBooking?._id
        let downloadError = null
        if (canDownloadTravelDocument) {
          try {
            await downloadTravelDocument(
              token,
              createdBooking._id,
              createdBooking.travelDocumentNo || createdBooking.bookingReference,
              "vehicle"
            )
          } catch (error) {
            downloadError = error
          }
        }

        Swal.fire({
          icon: downloadError ? 'warning' : 'success',
          title: downloadError ? 'Booking Confirmed' : successTitle,
          text: downloadError
            ? (downloadError.message || 'Booking confirmed, but vehicle travel document download failed.')
            : (canDownloadTravelDocument ? 'Vehicle travel document download started.' : successText),
          timer: 3000,
          showConfirmButton: false,
          confirmButtonColor: '#0ea5e9',
          cancelButtonColor: '#6c757d',
        }).then(() => {
          const closeBtn = document.querySelector("#myModal1 .btn-close")
          if (closeBtn) closeBtn.click()
          
          const updated = JSON.parse(JSON.stringify(searchResults))
          if (updated?.data?.trips) {
            updated.data.trips.forEach(tripItem => {
              if (tripItem.cabinOptions) {
                const tripId = tripItem.trip?._id
                tripItem.cabinOptions.forEach(opt => {
                  const key = `${tripId}-${opt.cabin?._id}`
                  if (selectedTrips.includes(key) && opt.availability) {
                    opt.availability.finalAvailableSeats = Math.max(0, (opt.availability.finalAvailableSeats || 0) - totalVehiclesQty)
                    opt.availability.availableSeats = Math.max(0, (opt.availability.availableSeats || 0) - totalVehiclesQty)
                  }
                })
              } else if (tripItem.returnTrip) {
                const outId = tripItem.returnTrip.outbound?._id
                const key = `${outId}-${tripItem.cabin?._id}`
                if (selectedTrips.includes(key) && tripItem.availability) {
                  tripItem.availability.outboundSeats = Math.max(0, (tripItem.availability.outboundSeats || 0) - totalVehiclesQty)
                  tripItem.availability.inboundSeats = Math.max(0, (tripItem.availability.inboundSeats || 0) - totalVehiclesQty)
                }
              }
            })
          }
          setSearchResults(updated)
          setSelectedTrips([])
          setSelectedPricingType({})
          setVehicleFormData({})

          navigate(location.pathname, { replace: true, state: { ...location.state, searchResults: updated } })
        })
      } else {
        Swal.fire({ icon: 'error', title: 'Booking Failed', text: response?.message || "Unknown error" })
      }
    } catch (err) {
      console.error("Booking error:", err)
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || "An error occurred while creating the booking." })
    } finally {
      setIsBooking(false)
    }
  }

  // Format date time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Get port name by ID
  const getPortName = (portId) => {
    const port = ports?.find((p) => p._id === portId)
    return port?.name || portId
  }

  // Get cabin name by ID
  const getCabinName = (cabinId) => {
    const cabin = cabins?.find((c) => c._id === cabinId)
    return cabin?.name || cabinId
  }

  // Get vehicle summary
  const getVehicleSummary = () => {
    if (!searchParams?.passengers || !payloadTypes) return "N/A"
    return searchParams.passengers
      .map((p) => {
        const type = payloadTypes.find((t) => t._id === p.payloadTypeId)
        return `${p.quantity} ${type?.name || "Vehicle"}`
      })
      .join(", ")
  }

  // Toggle trip selection with pricing type
  const toggleTripSelection = (tripId, cabinId, pricingType) => {
    const key = `${tripId}-${cabinId}`
    const isInboundSelection = key.startsWith("inbound-")
    const alreadySelected = selectedTrips.includes(key)

    setSelectedTrips((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key)
      }

      if (isReturnTrip && isInboundSelection) {
        return [...prev.filter((k) => !k.startsWith("inbound-")), key]
      }

      if (isReturnTrip) {
        return [...prev.filter((k) => k.startsWith("inbound-")), key]
      }

      return [...prev, key]
    })

    setSelectedPricingType((prev) => {
      const next = { ...prev }
      if (alreadySelected) {
        delete next[key]
        return next
      }

      if (isReturnTrip && isInboundSelection) {
        Object.keys(next).forEach((existingKey) => {
          if (existingKey.startsWith("inbound-")) delete next[existingKey]
        })
      } else if (isReturnTrip) {
        Object.keys(next).forEach((existingKey) => {
          if (!existingKey.startsWith("inbound-")) delete next[existingKey]
        })
      }

      next[key] = pricingType
      return next
    })
  }

  // Toggle trip details expansion
  const toggleExpand = (tripId) => {
    setExpandedTrips((prev) => ({
      ...prev,
      [tripId]: !prev[tripId],
    }))
  }

  // Determine trip type and extract trips appropriately
  const isReturnTrip = searchParams?.tripType === "return"
  let displayTrips = []

  if (isReturnTrip && Array.isArray(searchResults?.data?.trips)) {
    displayTrips = searchResults.data.trips.map((tripCombination) => ({
      returnTrip: {
        outbound: tripCombination.returnTrip?.outbound,
        inbound: tripCombination.returnTrip?.inbound,
      },
      cabin: tripCombination.cabin,
      availability: tripCombination.availability,
      pricing: tripCombination.pricing,
      passengers: tripCombination.passengers,
      bookingAllowed: tripCombination.bookingAllowed,
      totalPassengers: tripCombination.totalPassengers,
      ticketingRules: tripCombination.ticketingRules,
    })) || []
  } else if (!isReturnTrip && Array.isArray(searchResults?.data?.trips)) {
    displayTrips = searchResults.data.trips.flatMap((tripItem) =>
      (tripItem.cabinOptions || []).map((option) => ({
        trip: tripItem.trip,
        cabin: option.cabin,
        availability: option.availability,
        pricing: option.pricing,
        bookingAllowed: option.bookingAllowed,
        passengers: tripItem.passengers,
        totalPassengers: tripItem.totalPassengers,
        ticketingRules: tripItem.ticketingRules
      }))
    )
  }

  const getReturnSelectionKey = (tripData, direction) => {
    const trip = direction === "inbound" ? tripData.returnTrip?.inbound : tripData.returnTrip?.outbound
    const cabin = tripData.cabin
    if (!trip?._id || !cabin?._id) return null
    const key = `${trip._id}-${cabin._id}`
    return direction === "inbound" ? `inbound-${key}` : key
  }

  const getUniqueReturnTrips = (direction) => {
    const seen = new Set()
    return displayTrips.filter((tripData) => {
      const key = getReturnSelectionKey(tripData, direction)
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  const outboundDisplayTrips = isReturnTrip ? getUniqueReturnTrips("outbound") : displayTrips
  const inboundDisplayTrips = isReturnTrip ? getUniqueReturnTrips("inbound") : []

  const hasSelectedClosedTrip = displayTrips.some((tripData) => {
    const cabin = tripData.cabin
    if (!cabin) return false
    if (isReturnTrip) {
      const outboundTrip = tripData.returnTrip?.outbound
      const inboundTrip = tripData.returnTrip?.inbound
      const outboundKey = outboundTrip ? `${outboundTrip._id}-${cabin._id}` : null
      const inboundKey = inboundTrip ? `inbound-${inboundTrip._id}-${cabin._id}` : null
      return (selectedTrips.includes(outboundKey) || selectedTrips.includes(inboundKey)) && !tripData.bookingAllowed
    }
    const trip = tripData.trip
    if (!trip) return false
    return selectedTrips.includes(`${trip._id}-${cabin._id}`) && !tripData.bookingAllowed
  })

  const hasResults = displayTrips.length > 0

  return (
    <>
      <section className="features py-5">
        <div className="container-lg">
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .table>:not(caption)>*>* {
                  caption-side: bottom;
                  border-collapse: collapse;
                  background-color: var(--fcard)!important;
                }
                .table th {
                  vertical-align: bottom;
                  background-color: var(--bg-body)!important;
                  color:var(--text-color) !important;
                  white-space: nowrap;
                }
                .modal-content {
                  background:var(--bg-body);
                }
                .trip-details-expanded {
                  background-color: #f8f9fa;
                  border-left: 3px solid #0d6efd;
                }
                .price-breakdown {
                  background-color: #fff;
                  border-radius: 8px;
                }
                .status-badge {
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 0.75rem;
                  font-weight: 600;
                }
                .status-scheduled { background-color: #d1e7dd; color: #0f5132; }
                .status-departed { background-color: #f8d7da; color: #842029; }
                .status-boarding { background-color: #fff3cd; color: #664d03; }
              `,
            }}
          />
          <div className="row g-3 justify-content-center">
            <div className="col-lg-12">
              <h1 className="mb-4">Search Results</h1>

              {/* Search Summary */}
              {searchParams && (
                <div className="card shadow-sm custom-card mb-4">
                  <div className="card-body">
                    <p className="text-muted mb-0">
                      <strong>{searchParams.tripType === "return" ? "Round-trip" : "One-way"} Vehicle trip</strong> from{" "}
                      <strong>{getPortName(searchParams.originPort)}</strong> to{" "}
                      <strong>{getPortName(searchParams.destinationPort)}</strong> departing on{" "}
                      <strong>{searchParams.departureDate}</strong>
                      {searchParams.tripType === "return" && searchParams.returnDate && (
                        <> and returning on <strong>{searchParams.returnDate}</strong></>
                      )}
                      . Cabin: <strong>{getCabinName(searchParams.cabin)}</strong>. Visa Type:{" "}
                      <strong>{searchParams.visaType}</strong>. Vehicles: <strong>{getVehicleSummary()}</strong>.
                    </p>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end mb-4">
                <button
                  className="btn btn-primary-navy me-2 custom-blue-btn"
                  onClick={handlePreValidateBooking}
                  disabled={selectedTrips.length === 0 || hasSelectedClosedTrip}
                >
                  {hasSelectedClosedTrip ? "Booking Closed" : `Book Selected (${selectedTrips.length})`}
                </button>
                <Link to=".." className="btn btn-secondary">
                  Back to Search
                </Link>
              </div>

              {!hasResults ? (
                <div className="card shadow-sm custom-card">
                  <div className="card-body text-center py-5">
                    <h4 className="text-muted">No trips found</h4>
                    <p className="text-muted">Try adjusting your search criteria</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Outbound Trip Section */}
                  <div className="card shadow-sm custom-card mb-4">
                    <div className="card-body p-0">
                      <h3 className="card-title p-3 mb-0 border-bottom">
                        {isReturnTrip ? "Outbound Trip" : "Trip"} (
                          {outboundDisplayTrips.length} found)
                      </h3>
                      <div className="table-responsive">
                        <table className="table table-borderless align-middle mb-0 custom-results-table">
                          <thead className="text-secondary text-uppercase" style={{ fontSize: "0.8rem" }}>
                            <tr>
                              <th>Ship Name</th>
                              <th>Trip Code</th>
                              <th>Origin</th>
                              <th>Destination</th>
                              <th>ETD</th>
                              <th>ETA</th>
                              <th>Cabin</th>
                              <th>Vehicle</th>
                              <th>Visa</th>
                              <th>Available</th>
                              <th>Total Price</th>
                              <th>Status</th>
                              <th>Select</th>
                            </tr>
                          </thead>
                          <tbody>
                            {outboundDisplayTrips.map((tripData, index) => {
                              const trip = isReturnTrip ? tripData.returnTrip?.outbound : tripData.trip
                              const cabin = tripData.cabin
                              const availability = tripData.availability
                              const pricing = tripData.pricing
                              const passengers = tripData.passengers
                              const ticketingRules = isReturnTrip
                                ? (tripData.ticketingRules?.outbound || {})
                                : (tripData.ticketingRules || {})
                              const bookingAllowed = tripData.bookingAllowed
                              const pricingType = tripData.pricingType

                              if (!trip) return null

                              // Outbound deduplication: for return trips, show each outbound trip+cabin only once.
                              // displayTrips is NOT modified — all .find() lookups in booking/pre-validation use the full array.
                              return (
                                <React.Fragment key={`outbound-${trip._id}-${cabin._id}-${pricingType}-${index}`}>
                                  <tr className="trip-row">
                                    <td>
                                      <strong>{trip.ship?.name || "N/A"}</strong>
                                      <br />
                                      <small className="text-muted">{trip.ship?.imoNumber}</small>
                                    </td>
                                    <td>{trip.tripCode}</td>
                                    <td>
                                      {trip.departurePort?.name}
                                      <br />
                                      <small className="text-muted">({trip.departurePort?.code})</small>
                                    </td>
                                    <td>
                                      {trip.arrivalPort?.name}
                                      <br />
                                      <small className="text-muted">({trip.arrivalPort?.code})</small>
                                    </td>
                                    <td>{formatDateTime(trip.departureDateTime)}</td>
                                    <td>{formatDateTime(trip.arrivalDateTime)}</td>
                                    <td>
                                      <strong>{cabin.name}</strong>
                                    </td>
                                    <td>
                                      {passengers?.map((p) => `${p.quantity} ${p.payloadType?.name}`).join(", ") || "N/A"}
                                    </td>
                                    <td>
                                      {searchParams?.visaType || "N/A"}
                                    </td>
                                    <td>
                                      {(() => {
                                        const avail = isReturnTrip
                                          ? availability?.outboundSeats
                                          : availability?.finalAvailableSeats
                                        return (
                                          <span
                                            className={`badge ${avail > 5
                                                ? "bg-success"
                                                : avail > 0
                                                  ? "bg-warning"
                                                  : "bg-danger"
                                              }`}
                                          >
                                            {avail}
                                          </span>
                                        )
                                      })()}
                                    </td>
                                    <td>
                                      {pricing ? (
                                        <div className="d-flex gap-2" style={{ minHeight: '70px' }}>
                                          {pricing.refundable && pricing.refundable.available &&
                                            (!isReturnTrip || (pricing.refundable.outboundTotal ?? 0) > 0) && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[`${trip._id}-${cabin._id}`] === 'refundable' ? '3px solid #28a745' : '1px solid #28a745',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#f0f9f7',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(trip._id, cabin._id, 'refundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <span className="badge bg-success text-white mb-1" style={{ width: 'fit-content' }}>Refundable ✓</span>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.refundable.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.refundable.outboundTotal || 0).toFixed(2)
                                                    : (pricing.refundable.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                              </div>
                                            )}

                                          {pricing.nonRefundable &&
                                            (isReturnTrip
                                              ? (pricing.nonRefundable.outboundTotal ?? 0) > 0
                                              : (pricing.nonRefundable.available || pricing.nonRefundable.totalPrice > 0)) && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[`${trip._id}-${cabin._id}`] === 'nonRefundable' ? '3px solid #ffc107' : '1px solid #ffc107',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#fff8f0',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(trip._id, cabin._id, 'nonRefundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <div className="mb-1">
                                                  <span className="badge bg-warning text-dark" style={{ width: 'fit-content' }}>Non-Refundable</span>
                                                </div>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.nonRefundable.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.nonRefundable.outboundTotal || 0).toFixed(2)
                                                    : (pricing.nonRefundable.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                              </div>
                                            )}

                                          {(!pricing.refundable || !pricing.refundable.available) &&
                                            (!pricing.nonRefundable || (pricing.nonRefundable.available === false && !isReturnTrip)) && (
                                              <div className="flex-fill p-2 d-flex flex-column justify-content-center" style={{ border: '1px solid #ccc', borderRadius: '6px', backgroundColor: '#f8f9fa' }}>
                                                <strong>
                                                  {pricing.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.returnTotalPrice || 0).toFixed(2)
                                                    : (pricing.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                              </div>
                                            )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">Price N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      <span className={`status-badge status-${trip.status?.toLowerCase() || "scheduled"}`}>
                                        {trip.status}
                                      </span>
                                    </td>
                                    <td>
                                      {!selectedTrips.includes(`${trip._id}-${cabin._id}`) && (
                                        <small className="text-muted d-block">
                                          First select any price
                                        </small>
                                      )}
                                      {!bookingAllowed && (
                                        <small className="text-danger d-block" style={{ fontSize: "0.7rem" }}>
                                          Booking Closed
                                        </small>
                                      )}
                                    </td>
                                  </tr>

                                  {/* Expandable details row */}
                                  <tr className="details-row">
                                    <td colSpan={13} className="p-0">
                                      <div
                                        className="trip-details bg-white p-3 border-top"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => toggleExpand(`${trip._id}-${cabin._id}`)}
                                      >
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            <p className="mb-1 small">
                                              <strong>Check-in Opens:</strong>{" "}
                                              {formatDateTime(trip.checkInOpeningDate)} |{" "}
                                              <strong>Check-in Closes:</strong>{" "}
                                              {formatDateTime(trip.checkInClosingDate)} |{" "}
                                              <strong>Booking Closes:</strong>{" "}
                                              {formatDateTime(trip.bookingClosingDate)}
                                            </p>
                                          </div>
                                          <button className="btn btn-sm btn-outline-primary">
                                            {expandedTrips[`${trip._id}-${cabin._id}`] ? "Hide Details" : "Show Details"}
                                          </button>
                                        </div>

                                        {expandedTrips[`${trip._id}-${cabin._id}`] && (
                                          <div className="trip-details-expanded mt-3 p-3">
                                            <div className="row">
                                              <div className="col-md-6">
                                                <h6>Price Breakdown</h6>
                                                <div className="price-breakdown p-3">
                                                  {(() => {
                                                    let breakdownData = null
                                                    const selectedType = selectedPricingType[`${trip._id}-${cabin._id}`]

                                                    if (selectedType === 'refundable' && pricing?.refundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable }
                                                    } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.nonRefundable.breakdown, pricing: pricing.nonRefundable }
                                                    } else if (pricing?.refundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable }
                                                    } else if (pricing?.breakdown) {
                                                      breakdownData = { breakdown: pricing.breakdown, pricing: pricing }
                                                    }

                                                    return breakdownData?.breakdown?.length > 0 ? (
                                                      <>
                                                        {breakdownData.breakdown.map((item, idx) => (
                                                          <div key={idx} className="mb-2 pb-2 border-bottom">
                                                            <div className="d-flex justify-content-between">
                                                              <span>{item.payloadType?.name} x {item.quantity}</span>
                                                              <strong>
                                                                {breakdownData.pricing.currency?.currencyCode}{" "}
                                                                {isReturnTrip
                                                                  ? (item.outboundPrice?.subtotal || 0).toFixed(2)
                                                                  : (item.subtotal || 0).toFixed(2)
                                                                }
                                                              </strong>
                                                            </div>
                                                            <small className="text-muted">
                                                              Unit Price: {(isReturnTrip ? item.outboundPrice?.unitPrice : item.unitPrice || 0).toFixed(2)} | Unit Total:{" "}
                                                              {(isReturnTrip ? item.outboundPrice?.unitTotalPrice : item.unitTotalPrice || 0).toFixed(2)}
                                                            </small>
                                                          </div>
                                                        ))}
                                                        <div className="d-flex justify-content-between fw-bold">
                                                          <span>Total</span>
                                                          <span>
                                                            {breakdownData.pricing.currency?.currencyCode}{" "}
                                                            {isReturnTrip
                                                              ? (breakdownData.pricing.outboundTotal || 0).toFixed(2)
                                                              : (breakdownData.pricing.totalPrice || 0).toFixed(2)
                                                            }
                                                          </span>
                                                        </div>
                                                      </>
                                                    ) : (
                                                      <p className="text-muted mb-0">No pricing breakdown available</p>
                                                    )
                                                  })()}
                                                </div>

                                                <div className="mt-3">
                                                  <h6>Ticketing Rules</h6>
                                                  <div className="price-breakdown p-3">
                                                    {ticketingRules && Object.keys(ticketingRules).length > 0 ? (
                                                      <>
                                                        {ticketingRules.REFUND && (
                                                          <div className="mb-2">
                                                            <strong>Refund:</strong> {ticketingRules.REFUND.ruleName}
                                                          </div>
                                                        )}
                                                        {ticketingRules.VOID && (
                                                          <div className="mb-2">
                                                            <strong>Void:</strong> {ticketingRules.VOID.ruleName}
                                                          </div>
                                                        )}
                                                        {ticketingRules.REISSUE && (
                                                          <div className="mb-2">
                                                            <strong>Reissue:</strong> {ticketingRules.REISSUE.ruleName}
                                                          </div>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <p className="text-muted mb-0">No ticketing rules available</p>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {isReturnTrip && (
                    <div className="card shadow-sm custom-card mb-4">
                      <div className="card-body p-0">
                        <h3 className="card-title p-3 mb-0 border-bottom">
                          Inbound Trip ({inboundDisplayTrips.length} found)
                        </h3>
                        <div className="table-responsive">
                          <table className="table table-borderless align-middle mb-0 custom-results-table">
                            <thead className="text-secondary text-uppercase" style={{ fontSize: "0.8rem" }}>
                              <tr>
                                <th>Ship Name</th>
                                <th>Trip Code</th>
                                <th>Origin</th>
                                <th>Destination</th>
                                <th>ETD</th>
                                <th>ETA</th>
                                <th>Cabin</th>
                                <th>Vehicle</th>
                                <th>Visa</th>
                                <th>Available</th>
                                <th>Total Price</th>
                                <th>Status</th>
                                <th>Select</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inboundDisplayTrips.map((tripData, index) => {
                                const trip = tripData.returnTrip?.inbound
                                const cabin = tripData.cabin
                                const availability = tripData.availability
                                const pricing = tripData.pricing
                                const passengers = tripData.passengers
                                const bookingAllowed = tripData.bookingAllowed
                                const pricingType = tripData.pricingType

                                if (!trip) return null
                                const inboundKey = `inbound-${trip._id}-${cabin._id}`

                                return (
                                  <React.Fragment key={`inbound-${trip._id}-${cabin._id}-${pricingType}-${index}`}>
                                    <tr className="trip-row">
                                      <td>
                                        <strong>{trip.ship?.name || "N/A"}</strong>
                                      </td>
                                      <td>{trip.tripCode}</td>
                                      <td>{trip.departurePort?.name}</td>
                                      <td>{trip.arrivalPort?.name}</td>
                                      <td>{formatDateTime(trip.departureDateTime)}</td>
                                      <td>{formatDateTime(trip.arrivalDateTime)}</td>
                                      <td><strong>{cabin.name}</strong></td>
                                      <td>
                                        {passengers?.map((p) => `${p.quantity} ${p.payloadType?.name}`).join(", ") || "N/A"}
                                      </td>
                                      <td>
                                        {searchParams?.visaType || "N/A"}
                                      </td>
                                      <td>
                                        {(() => {
                                          const avail = availability?.inboundSeats
                                          return (
                                            <span className={`badge ${avail > 5 ? "bg-success" : avail > 0 ? "bg-warning" : "bg-danger"}`}>
                                              {avail}
                                            </span>
                                          )
                                        })()}
                                      </td>
                                      <td>
                                        {pricing ? (
                                          <div className="d-flex gap-2" style={{ minHeight: '70px' }}>
                                            {pricing.refundable && pricing.refundable.available && (pricing.refundable.inboundTotal ?? 0) > 0 && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[inboundKey] === 'refundable' ? '3px solid #28a745' : '1px solid #28a745',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#f0f9f7',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(`inbound-${trip._id}`, cabin._id, 'refundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <span className="badge bg-success text-white mb-1" style={{ width: 'fit-content' }}>Refundable</span>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.refundable.currency?.currencyCode || ""} {(pricing.refundable.inboundTotal).toFixed(2)}
                                                </strong>
                                                {selectedPricingType[inboundKey] === 'refundable' && (
                                                  <span className="badge bg-success mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                )}
                                              </div>
                                            )}
                                            {pricing.nonRefundable && (pricing.nonRefundable.inboundTotal ?? 0) > 0 && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[inboundKey] === 'nonRefundable' ? '3px solid #ffc107' : '1px solid #ffc107',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#fff8f0',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(`inbound-${trip._id}`, cabin._id, 'nonRefundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <span className="badge bg-warning text-dark mb-1" style={{ width: 'fit-content' }}>Non-Refundable</span>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.nonRefundable.currency?.currencyCode || ""} {(pricing.nonRefundable.inboundTotal).toFixed(2)}
                                                </strong>
                                                {selectedPricingType[inboundKey] === 'nonRefundable' && (
                                                  <span className="badge bg-warning text-dark mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : "N/A"}
                                      </td>
                                      <td>{trip.status}</td>
                                      <td>
                                        {!selectedTrips.includes(inboundKey) && (
                                          <small className="text-muted d-block">First select any price</small>
                                        )}
                                        {!bookingAllowed && (
                                          <small className="text-danger d-block" style={{ fontSize: "0.7rem" }}>
                                            Booking Closed
                                          </small>
                                        )}
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <div className="modal modal-lg" id="myModal1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Vehicle Booking Information</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal" style={{ background: "#fff" }}>
                X
              </button>
            </div>
            <div className="modal-body">
              <form>
                <p className="text-muted small mb-3">
                  Please fill vehicle details for all selected vehicles: {getVehicleSummary()}.
                </p>
                {getExpectedVehicleSlots().map(({ key, payloadType, sequence }, formIndex) => {
                  const form = vehicleFormData[key] || createEmptyVehicleForm(payloadType)
                  return (
                    <div key={key} className="border rounded p-3 mb-4">
                      <h6 className="border-bottom pb-2 mb-3 text-secondary uppercase fw-bold" style={{ fontSize: '0.85rem' }}>
                        {payloadType?.name || "Vehicle"} {sequence} ({formIndex + 1} of {getExpectedVehicleSlots().length})
                      </h6>

                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Make/Model <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="e.g. Toyota Hilux 2024" value={form.makeModel} onChange={(e) => handleVehicleChange(key, "makeModel", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Engine/Chassis No</label>
                          <input type="text" className="form-control" placeholder="Engine/Chassis No" value={form.engineNo} onChange={(e) => handleVehicleChange(key, "engineNo", e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Plate No <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="e.g. ABC-1234" value={form.vehicleRegistration} onChange={(e) => handleVehicleChange(key, "vehicleRegistration", e.target.value)} required />
                        </div>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 text-secondary uppercase fw-bold" style={{ fontSize: '0.85rem' }}>Owner Details</h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Name <span className="text-danger">*</span></label>
                          <input type="text" className="form-control" placeholder="Full Name" value={form.ownerName} onChange={(e) => handleVehicleChange(key, "ownerName", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Phone <span className="text-danger">*</span></label>
                          <input type="tel" className="form-control" placeholder="Phone Number" value={form.ownerPhone} onChange={(e) => handleVehicleChange(key, "ownerPhone", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">Passport/Residency No</label>
                          <input type="text" className="form-control" placeholder="Passport No" value={form.ownerPassport} onChange={(e) => handleVehicleChange(key, "ownerPassport", e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label text-muted small mb-1">License No</label>
                          <input type="text" className="form-control" placeholder="Driver's License No" value={form.ownerLicense} onChange={(e) => handleVehicleChange(key, "ownerLicense", e.target.value)} />
                        </div>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 text-secondary uppercase fw-bold" style={{ fontSize: '0.85rem' }}>Consignee Details</h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Name</label>
                          <input type="text" className="form-control" placeholder="Name" value={form.consigneeName} onChange={(e) => handleVehicleChange(key, "consigneeName", e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Phone</label>
                          <input type="text" className="form-control" placeholder="Phone" value={form.consigneePhone} onChange={(e) => handleVehicleChange(key, "consigneePhone", e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">ID No</label>
                          <input type="text" className="form-control" placeholder="ID No" value={form.consigneeIdNo} onChange={(e) => handleVehicleChange(key, "consigneeIdNo", e.target.value)} />
                        </div>
                      </div>

                      <h6 className="border-bottom pb-2 mb-3 text-secondary uppercase fw-bold" style={{ fontSize: '0.85rem' }}>Consignor Details</h6>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Name</label>
                          <input type="text" className="form-control" placeholder="Name" value={form.consignorName} onChange={(e) => handleVehicleChange(key, "consignorName", e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">Phone</label>
                          <input type="text" className="form-control" placeholder="Phone" value={form.consignorPhone} onChange={(e) => handleVehicleChange(key, "consignorPhone", e.target.value)} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label text-muted small mb-1">ID No</label>
                          <input type="text" className="form-control" placeholder="ID No" value={form.consignorIdNo} onChange={(e) => handleVehicleChange(key, "consignorIdNo", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </form>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Cancel
                </button>
                {!isB2C && (
                  <button type="button" className="btn btn-warning text-dark" style={{ border: "none" }} onClick={() => onSubmitBooking(true)} disabled={isBooking}>
                    {isBooking ? "Processing..." : "Create Temporary Booking (Hold)"}
                  </button>
                )}
                <button type="button" className="btn text-white" style={{ backgroundColor: "#0b2046", border: "none" }} onClick={() => onSubmitBooking(isB2C ? true : false)} disabled={isBooking}>
                  {isBooking ? "Processing..." : isB2C ? "Confirm Booking Request" : "Proceed to Payment"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer socialMedia={companyInfo?.socialMedia} />
    </>
  )
}

export default SearchResultVehicle
